import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router';

type Props = {
  $color: 'primary' | 'secondary';
  $type: 'contained' | 'outline';
  $disabled?: boolean;
  $size?: 'small' | 'medium' | 'large';
};

const rotate = keyframes`
  from { transform: rotate(0deg); } 
  to { transform: rotate(360deg); }
`;

const commonButtonStyles = css<Props>`
  align-items: center;
  background-color: unset;
  border-radius: 100px;
  color: ${({ theme }) => theme.palette.common.black};
  display: flex;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily};
  font-optical-sizing: auto;
  font-size: ${({ $size, theme }) =>
    theme.utils.pxToRem($size === 'small' ? 12 : 16)};
  font-style: normal;
  font-weight: bold;
  justify-content: center;
  line-height: ${({ $size, theme }) =>
    theme.utils.pxToRem($size === 'small' ? 20 : 24)};
  padding: ${({ $size }) => ($size === 'small' ? '8px 12px' : '10px 24px')};
  text-align: center;
  transition:
    background-color 300ms ease-in-out,
    border-color 300ms ease-in-out,
    color 300ms ease-in-out;

  & span + * {
    margin-left: 8px;
  }
`;

const containedButtonStyles = css<Props>`
  ${commonButtonStyles};
  color: ${({ $color, $disabled, theme }) =>
    $disabled
      ? theme.palette.common.white
      : $color === 'secondary'
        ? theme.palette.common.black
        : theme.palette.common.white};
  background-color: ${({ $color, $disabled, theme }) =>
    $disabled
      ? theme.palette.secondary.gray
      : $color === 'secondary'
        ? theme.palette.common.white
        : theme.palette.common.black};
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    color: ${({ $color, $disabled, theme }) =>
      $disabled
        ? theme.palette.common.white
        : $color === 'secondary'
          ? theme.palette.common.black
          : theme.palette.common.white};
    background-color: ${({ $disabled, theme }) =>
      $disabled ? theme.palette.secondary.gray : theme.palette.primary.bold};
  }
`;

const outlineButtonStyles = css<Props>`
  ${commonButtonStyles};
  border: ${({ theme }) => `1px solid ${theme.palette.primary.gray}`};

  &:hover {
    border-color: ${({ theme }) => theme.palette.primary.bold};
  }
`;

export const StyledLinkButton = styled(Link)<Props>`
  ${({ $type }) =>
    $type === 'outline' ? outlineButtonStyles : containedButtonStyles};
  text-decoration: none;
`;
export const StyledButton = styled.button<Props>`
  ${({ $type }) =>
    $type === 'outline' ? outlineButtonStyles : containedButtonStyles};
`;
export const StyledLoading = styled.span`
  animation: ${rotate} 1s linear infinite;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.palette.primary.light};
  border-top: 2px solid ${({ theme }) => theme.palette.primary.bold};
  display: block;
  height: 24px;
  width: 24px;
`;
